import { visit } from "@hotwired/turbo";
import Breadcrumbs from "../shared/Breadcrumbs";

// #STYLE_GUIDE: TypeScript interfaces use camelCase (props are transformed by turbo-mount)
interface BrainProps {
  id: string;
  name: string;
  content: string;
  enabled: boolean;
  avatar: string;
  avatarId: string;
  createdAt: string;
  updatedAt: string;
}

interface BrainShowProps {
  brain: BrainProps;
}

// #STYLE_GUIDE: Show component displays single record details
// - Uses daisyUI components for consistent styling
// - Navigation via Turbo.visit() for SPA-like experience
export default function BrainShow({ brain }: BrainShowProps) {
  const handleEdit = () => {
    // #TURBO: Use Turbo.visit for navigation
    visit(`/brains/${brain.id}/edit`);
  };

  const handleBack = () => {
    // #TURBO: Use Turbo.visit for navigation
    visit(`/avatars/${brain.avatarId}`);
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumbs */}
      <Breadcrumbs
        items={[
          { label: brain.avatar, path: `/avatars/${brain.avatarId}` },
          { label: brain.name },
        ]}
      />

      {/* Header with actions */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Brain Details</h1>
        <div className="join">
          <button onClick={handleBack} className="btn btn-outline join-item">
            Back to Avatar
          </button>
          <button onClick={handleEdit} className="btn btn-primary join-item">
            Edit
          </button>
        </div>
      </div>

      {/* Details card */}
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">
                <span className="label-text font-semibold">ID</span>
              </label>
              <p className="text-base-content">{brain.id}</p>
            </div>

            <div>
              <label className="label">
                <span className="label-text font-semibold">Name</span>
              </label>
              <p className="text-base-content">{brain.name || "Not set"}</p>
            </div>

            <div>
              <label className="label">
                <span className="label-text font-semibold">Content</span>
              </label>
              <div className="text-base-content whitespace-pre-wrap">
                {brain.content || "Not set"}
              </div>
            </div>

            <div>
              <label className="label">
                <span className="label-text font-semibold">Enabled</span>
              </label>
              <p className="text-base-content">
                <span
                  className={`badge ${
                    brain.enabled ? "badge-success" : "badge-error"
                  }`}
                >
                  {brain.enabled ? "Yes" : "No"}
                </span>
              </p>
            </div>

            <div>
              <label className="label">
                <span className="label-text font-semibold">Avatar</span>
              </label>
              <p className="text-base-content">
                {(() => {
                  // Handle reference field display - show displayName if it's an object, otherwise show the value
                  const value = brain.avatar;
                  if (
                    typeof value === "object" &&
                    value !== null &&
                    "displayName" in value
                  ) {
                    return value.displayName;
                  }
                  return value || "Not set";
                })()}
              </p>
            </div>

            <div>
              <label className="label">
                <span className="label-text font-semibold">Created at</span>
              </label>
              <p className="text-base-content">
                {brain.createdAt || "Not set"}
              </p>
            </div>

            <div>
              <label className="label">
                <span className="label-text font-semibold">Updated at</span>
              </label>
              <p className="text-base-content">
                {brain.updatedAt || "Not set"}
              </p>
            </div>

            <div>
              <label className="label">
                <span className="label-text font-semibold">Created At</span>
              </label>
              <p className="text-base-content">
                {new Date(brain.createdAt).toLocaleString()}
              </p>
            </div>

            <div>
              <label className="label">
                <span className="label-text font-semibold">Updated At</span>
              </label>
              <p className="text-base-content">
                {new Date(brain.updatedAt).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
